import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { listIntegrations } from "../api/integrations";
import { batchUploadCDDI } from "../api/cddi";
import { WorkgroupSelect } from "./WorkgroupSelect";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select } from "./ui/select";
import { Badge } from "./ui/badge";
import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Download,
  FileText,
  FolderOpen,
  Gauge,
  Image as ImageIcon,
  Layers,
  Sparkles,
  Upload,
} from "lucide-react";

const MAX_FILENAME_BADGES = 6;

const CDDIBatchUpload = () => {
  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState([]);
  const [integrationsError, setIntegrationsError] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);

  const [workgroupMap, setWorkgroupMap] = useState({});
  const [workgroupsLoading, setWorkgroupsLoading] = useState(false);

  const [selectedWorkgroup, setSelectedWorkgroup] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [files, setFiles] = useState([]);
  const [numBatches, setNumBatches] = useState("1");
  const [maxConcurrent, setMaxConcurrent] = useState("");
  const [testName, setTestName] = useState("");
  const [trainingProcessImprovement, setTrainingProcessImprovement] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [guidanceMap, setGuidanceMap] = useState(null);
  const [guidanceStatus, setGuidanceStatus] = useState("");

  useEffect(() => {
    let isMounted = true;
    const loadIntegrations = async () => {
      setIntegrationsLoading(true);
      setIntegrationsError(null);

      try {
        const data = await listIntegrations();
        if (!isMounted) return;
        const safeList = Array.isArray(data) ? data : [];
        setIntegrations(safeList);
        if (safeList.length === 1) {
          setSelectedIntegration(safeList[0].id);
        }
      } catch (e) {
        if (!isMounted) return;
        console.error("[CDDIBatchUpload] Failed to load integrations", e);
        setIntegrations([]);
        setIntegrationsError(
          e?.message || "Unable to load integrations right now."
        );
      } finally {
        if (!isMounted) return;
        setIntegrationsLoading(false);
      }
    };

    loadIntegrations();
    return () => {
      isMounted = false;
    };
  }, []);

  const guidanceEntryCount = useMemo(
    () => (guidanceMap ? Object.keys(guidanceMap).length : 0),
    [guidanceMap]
  );

  const filePreviewNames = useMemo(() => {
    if (!Array.isArray(files) || files.length === 0) return [];
    return files.slice(0, MAX_FILENAME_BADGES).map((file) => file.name);
  }, [files]);

  const integrationName = useMemo(() => {
    if (!selectedIntegration) return "";
    const match = integrations.find((item) => item.id === selectedIntegration);
    return match?.name || match?.id || "";
  }, [integrations, selectedIntegration]);

  const selectedWorkgroupName = useMemo(() => {
    if (!selectedWorkgroup) return "";
    return workgroupMap[selectedWorkgroup] || selectedWorkgroup;
  }, [selectedWorkgroup, workgroupMap]);

  const requirementMessage = useMemo(() => {
    if (!selectedWorkgroup) return "Select a workgroup to target this upload.";
    if (!selectedIntegration) return "Choose an integration to scope the run.";
    if (!testName.trim()) return "Enter a test name to identify this batch.";
    if (!files.length) return "Attach at least one image to begin.";
    return selectedWorkgroupName
      ? `Workgroup: ${selectedWorkgroupName}`
      : `Workgroup: ${selectedWorkgroup}`;
  }, [files.length, selectedIntegration, selectedWorkgroup, selectedWorkgroupName, testName]);

  const heroHighlights = useMemo(
    () => [
      {
        id: "images",
        label: "Images ready",
        value: files.length ? files.length.toString() : "0",
        description: files.length
          ? "Ready for batch upload"
          : "Attach image set to begin",
        icon: ImageIcon,
      },
      {
        id: "guidance",
        label: "Guidance pairs",
        value: guidanceEntryCount ? guidanceEntryCount.toString() : "0",
        description: guidanceEntryCount
          ? "JSON guidance detected"
          : "Optional guidance JSON",
        icon: Sparkles,
      },
      {
        id: "batches",
        label: "Target batches",
        value: numBatches ? `×${numBatches}` : "—",
        description: integrationName
          ? `Integration ${integrationName}`
          : "Choose an integration",
        icon: Layers,
      },
    ],
    [files.length, guidanceEntryCount, numBatches, integrationName]
  );

  const onWorkgroupsLoaded = useCallback((items) => {
    if (!Array.isArray(items)) return;
    const mapping = items.reduce((acc, item) => {
      acc[item.id] = item.name || item.id;
      return acc;
    }, {});
    setWorkgroupMap(mapping);
  }, []);

  const onPickFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    const imageFiles = arr.filter((file) =>
      /\.(png|jpe?g|gif|bmp|webp)$/i.test(file.name)
    );
    const jsonFiles = arr.filter((file) => /\.(json)$/i.test(file.name));

    setFiles(imageFiles);
    setResult(null);
    setError(null);
    setGuidanceStatus("");
    setGuidanceMap(null);

    if (jsonFiles.length > 0) {
      setGuidanceStatus("Processing guidance JSON…");
      Promise.all(jsonFiles.map((file) => file.text().catch(() => null)))
        .then((texts) => {
          const raw = {};
          texts
            .filter(Boolean)
            .forEach((text) => {
              try {
                const data = JSON.parse(String(text));
                if (data && typeof data === "object" && !Array.isArray(data)) {
                  Object.entries(data).forEach(([key, value]) => {
                    if (value != null) raw[String(key)] = String(value);
                  });
                } else if (Array.isArray(data)) {
                  data.forEach((item) => {
                    if (
                      item &&
                      typeof item === "object" &&
                      !Array.isArray(item)
                    ) {
                      const path =
                        item.path ||
                        item.path_to_image ||
                        item.image ||
                        item.file;
                      const guidance =
                        item.guidance || item.hint || item.instruction;
                      if (path != null && guidance != null) {
                        raw[String(path)] = String(guidance);
                      }
                    } else if (Array.isArray(item) && item.length >= 2) {
                      raw[String(item[0])] = String(item[1]);
                    }
                  });
                }
              } catch (parseErr) {
                console.warn(
                  "[CDDIBatchUpload] Failed to parse guidance JSON fragment",
                  parseErr
                );
              }
            });

          const normalizePath = (value) =>
            String(value || "").replace(/\\/g, "/").replace(/^\.\//, "");
          const normalized = {};
          Object.entries(raw).forEach(([path, value]) => {
            normalized[normalizePath(path)] = value;
          });
          setGuidanceMap(normalized);
          const count = Object.keys(normalized).length;
          setGuidanceStatus(
            count
              ? `Loaded guidance for ${count} path${count === 1 ? "" : "s"}`
              : "No guidance pairs found in JSON"
          );
        })
        .catch((err) => {
          console.error("[CDDIBatchUpload] Failed to read guidance JSON", err);
          setGuidanceMap(null);
          setGuidanceStatus("Failed to read guidance JSON");
        });
    }
  }, []);

  const onSubmit = async (event) => {
    event?.preventDefault?.();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      if (!selectedIntegration) {
        throw new Error("Select an integration before uploading.");
      }
      if (!selectedWorkgroup) {
        throw new Error("Select a workgroup before uploading.");
      }
      if (!testName.trim()) {
        throw new Error("Enter a descriptive test name.");
      }
      if (!files || files.length === 0) {
        throw new Error("Add at least one image to upload.");
      }

      console.info("[CDDIBatchUpload] Starting upload", {
        images: files.length,
        hasGuidanceJson: Boolean(guidanceMap),
        guidanceEntries: guidanceMap ? Object.keys(guidanceMap).length : 0,
        testName,
        numBatches,
        maxConcurrent,
      });

      const payload = {
        images: files,
        numBatches: Number(numBatches) || 1,
        maxConcurrent: maxConcurrent ? Number(maxConcurrent) : undefined,
        testName: testName.trim(),
        integrationId: selectedIntegration,
        workgroup: selectedWorkgroup,
        trainingProcessImprovement,
        guidanceMap: guidanceMap || undefined,
      };

      const response = await batchUploadCDDI(payload);
      console.info("[CDDIBatchUpload] Upload completed", response);
      setResult(response);
    } catch (err) {
      console.error("[CDDIBatchUpload] Upload failed", err);
      setError(err?.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadGuidanceTemplate = useCallback(() => {
    try {
      const normalizePath = (value) =>
        String(value || "").replace(/\\/g, "/").replace(/^\.\//, "");
      const template = {};
      (files || []).forEach((file) => {
        const rel =
          normalizePath(
            file.webkitRelativePath ||
              file.relativePath ||
              file.path ||
              file.name
          ) || "";
        if (rel) {
          template[rel] = "";
        }
      });

      const json = JSON.stringify(template, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const base = testName
        ? testName.trim().replace(/[^a-z0-9-_]+/gi, "_")
        : "cddi-guidance";
      anchor.href = url;
      anchor.download = `${base}-template.json`;
      document.body.appendChild(anchor);
      anchor.click();
      setTimeout(() => {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err) {
      console.error("[CDDIBatchUpload] Failed to generate guidance template", err);
    }
  }, [files, testName]);

  const missingRequired =
    !selectedWorkgroup || !selectedIntegration || !testName.trim() || !files.length;
  const submitDisabled =
    submitting || workgroupsLoading || integrationsLoading || missingRequired;

  return (
    <div className="space-y-8 px-6 pb-16" data-testid="cddi-batch-page">
      <section
        className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-6 shadow-sm"
        data-testid="cddi-batch-hero"
      >
        <div
          className="pointer-events-none absolute -top-24 right-12 h-48 w-48 rounded-full bg-primary/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-16 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Batch automation console
              </div>
              <h1 className="text-3xl font-semibold tracking-tight">
                CDDI batch orchestrator
              </h1>
              <p className="max-w-2xl text-sm text-muted-foreground">
                Upload curated image sets, attach optional guidance, and create
                orchestrated batches for large challenge runs. Queue every file
                as a dedicated CDDI challenge while preserving grouping and
                concurrency controls.
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                onClick={() => navigate("/cddi-results")}
                className="inline-flex items-center"
                data-testid="cddi-batch-view-results"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View batch results
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/cddi-demo")}
                className="inline-flex items-center"
                data-testid="cddi-batch-go-demo"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Run single challenge
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {heroHighlights.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-border/60 bg-background/70 p-4 shadow-sm backdrop-blur"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <item.icon className="h-4 w-4 text-primary" />
                  {item.label}
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold">{item.value}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.description}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[420px,1fr]">
        <Card data-testid="cddi-batch-config-card">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Upload className="h-4 w-4 text-primary" />
              Batch configuration
            </div>
            <CardTitle>Define the upload parameters</CardTitle>
            <CardDescription>
              Select the workgroup and integration context, then choose batch
              sizing rules for the generated challenges.
            </CardDescription>
          </CardHeader>
          <form
            onSubmit={onSubmit}
            className="space-y-6"
            data-testid="cddi-batch-form"
          >
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <WorkgroupSelect
                  selectedWorkgroup={selectedWorkgroup}
                  onSelect={setSelectedWorkgroup}
                  onWorkgroupsLoaded={onWorkgroupsLoaded}
                  onLoadingChange={setWorkgroupsLoading}
                  label="Workgroup"
                  dataTestId="cddi-batch-workgroup-select"
                />

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground px-1">
                  Integration
                </label>
                <Select
                  value={selectedIntegration}
                  onChange={(event) =>
                    setSelectedIntegration(event.target.value)
                  }
                  disabled={integrationsLoading}
                  data-testid="cddi-batch-integration-select"
                >
                  <option value="">
                    {integrationsLoading
                      ? "Loading integrations…"
                      : "Select an integration"}
                  </option>
                  {integrations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name || item.id}
                    </option>
                  ))}
                </Select>
                {integrationsError && (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {integrationsError}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="cddi-batch-num-batches"
                  className="text-xs font-medium text-muted-foreground px-1"
                >
                  Number of batches
                </label>
                <Input
                  id="cddi-batch-num-batches"
                  type="number"
                  min={1}
                  value={numBatches}
                  onChange={(event) => setNumBatches(event.target.value)}
                  data-testid="cddi-batch-num-batches"
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="cddi-batch-test-name"
                  className="text-xs font-medium text-muted-foreground px-1"
                >
                  Test name
                </label>
                <Input
                  id="cddi-batch-test-name"
                  type="text"
                  placeholder="e.g. regression-2025-09-11"
                  value={testName}
                  onChange={(event) => setTestName(event.target.value)}
                  data-testid="cddi-batch-test-name"
                />
              </div>
            </div>

            <div className="space-y-4 rounded-lg border border-dashed border-border/60 bg-muted/20 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Gauge className="h-4 w-4 text-primary" />
                Advanced controls
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="cddi-batch-max-concurrent"
                    className="text-xs font-medium text-muted-foreground px-1"
                  >
                    Max concurrent challenges (optional)
                  </label>
                  <Input
                    id="cddi-batch-max-concurrent"
                    type="number"
                    min={1}
                    value={maxConcurrent}
                    placeholder="e.g. 5"
                    onChange={(event) =>
                      setMaxConcurrent(event.target.value)
                    }
                    data-testid="cddi-batch-max-concurrent"
                  />
                </div>
                <label className="flex items-center gap-2 rounded-md border border-border/60 bg-background px-3 py-2 text-sm shadow-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border border-muted-foreground/40"
                    checked={trainingProcessImprovement}
                    onChange={(event) =>
                      setTrainingProcessImprovement(event.target.checked)
                    }
                    data-testid="cddi-batch-training-improvement"
                  />
                  Training process improvement
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure concurrency limits to throttle challenge creation and
                flag uploads for process improvement analysis when needed.
              </p>
            </div>
            </CardContent>
            <CardFooter className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-xs text-muted-foreground">
                {requirementMessage}
              </div>
              <Button
                type="submit"
                disabled={submitDisabled}
                data-testid="cddi-batch-submit"
                className="w-full md:w-auto"
              >
                {submitting ? "Uploading…" : "Start upload"}
              </Button>
            </CardFooter>
          </form>
        </Card>

        <Card data-testid="cddi-batch-assets-card">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <FolderOpen className="h-4 w-4 text-primary" />
              Assets &amp; guidance
            </div>
            <CardTitle>Attach challenge artifacts</CardTitle>
            <CardDescription>
              Upload a folder or multiple images. Optionally include JSON files
              that map image paths to guidance strings.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground px-1">
                  Select images
                </div>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => onPickFiles(event.target.files)}
                  data-testid="cddi-batch-image-input"
                />
                <p className="text-xs text-muted-foreground">
                  Drag-and-drop supported. Images become individual CDDI
                  challenges.
                </p>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground px-1">
                  Or select a folder
                </div>
                <Input
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={(event) => onPickFiles(event.target.files)}
                  data-testid="cddi-batch-folder-input"
                />
                <p className="text-xs text-muted-foreground">
                  Include a JSON file with guidance pairs to auto-attach hints.
                </p>
              </div>
            </div>

            {files.length > 0 && (
              <div
                className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4"
                data-testid="cddi-batch-file-summary"
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  {files.length} image{files.length === 1 ? "" : "s"} selected
                </div>
                <div className="flex flex-wrap gap-2">
                  {filePreviewNames.map((name) => (
                    <Badge
                      key={name}
                      variant="secondary"
                      className="bg-primary/10 text-primary"
                    >
                      {name}
                    </Badge>
                  ))}
                  {files.length > MAX_FILENAME_BADGES && (
                    <Badge variant="outline">
                      +{files.length - MAX_FILENAME_BADGES} more
                    </Badge>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadGuidanceTemplate}
                  className="inline-flex items-center"
                  data-testid="cddi-batch-download-template"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download guidance JSON template
                </Button>
              </div>
            )}

            {guidanceStatus && (
              <div
                className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
                data-testid="cddi-batch-guidance-status"
              >
                <FileText className="h-4 w-4 text-primary" />
                {guidanceStatus}
              </div>
            )}

            {error && (
              <div
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="cddi-batch-error"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {result && (
        <Card data-testid="cddi-batch-result-summary">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Upload complete
            </div>
            <CardTitle>Created batches for {result.test_name}</CardTitle>
            <CardDescription>
              {`Generated ${result.num_batches} batch${
                result.num_batches === 1 ? "" : "es"
              } covering ${result.total_images} image${
                result.total_images === 1 ? "" : "s"
              }.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(result.batches || []).map((batch) => (
              <div
                key={batch.batch_num}
                className="rounded-lg border border-border/60 bg-background/80 p-4 shadow-sm"
                data-testid={`cddi-batch-result-${batch.batch_num}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Layers className="h-4 w-4 text-primary" />
                    Batch {batch.batch_num}
                  </div>
                  <Badge variant="outline">{batch.count} challenges</Badge>
                </div>
                {Array.isArray(batch.challenge_ids) &&
                  batch.challenge_ids.length > 0 && (
                    <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                      <div className="font-medium text-foreground">
                        Challenge IDs
                      </div>
                      <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-3 font-mono break-words">
                        {batch.challenge_ids.join(", ")}
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </CardContent>
          <CardFooter className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              Workgroup: {selectedWorkgroupName || selectedWorkgroup}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/cddi-results")}
              className="inline-flex items-center"
              data-testid="cddi-batch-view-results-footer"
            >
              <BarChart3 className="mr-2 h-4 w-4" />
              Inspect in results
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

export default CDDIBatchUpload;
